import React from 'react';
import styled from 'styled-components';
import { ThemeProvider } from '@oneaudi/unified-web-common';

import type { AppContent, AppSpaceTypes, AppThemeTypes } from '../../types';
import { useTracking } from '../../utils/useTracking';
import { getBackgroundColor } from '../../utils/getBackgroundColor';
import { getThemeName } from '../../../alpha-helper';

const spacingValues: Record<AppSpaceTypes, string> = {
  xxs: '--spacing-relative-2xs',
  xs: '--spacing-relative-xs',
  s: '--spacing-relative-sm',
  m: '--spacing-relative-md',
  l: '--spacing-relative-lg',
  xl: '--spacing-relative-xl',
  xxl: '--spacing-relative-2xl',
  xxxl: '--spacing-relative-3xl',
  '2xxxl': '--spacing-relative-6xl',
};

const StyledSpacer = styled.div<{
  appSpace: AppSpaceTypes;
  appTheme: AppThemeTypes;
}>`
  ${(props) => {
    return ` height: var(${spacingValues[props.appSpace]});`;
  }};

  ${(props) => `background-color: ${getBackgroundColor(props.appTheme)};`};
`;

export const Spacer: React.FC<AppContent> = (props) => {
  const { appSpace, appTheme } = props;
  const ref = React.useRef(null);
  const tracking = useTracking(ref, 'spacer-fa', {
    implementer: 37,
  });

  React.useEffect(() => {
    tracking.sendReadyEvent();
    tracking.registerImpressionTracking();
    /* eslint-disable-next-line react-hooks/exhaustive-deps --
     * We skip the rule here because we just want to run the code once and not define a dependency
     * here. Omitting the entire dependency array or adding the `tracking` object as dependency
     * would run the code more than once, causing issues with registering impression tracking event.
     **/
  }, []);

  return (
    <ThemeProvider theme={{ name: getThemeName(appTheme) }}>
      <StyledSpacer
        ref={ref}
        data-test-id="spacer"
        appSpace={appSpace}
        appTheme={appTheme}
        aria-hidden="true"
      >
        &nbsp;
      </StyledSpacer>
    </ThemeProvider>
  );
};
